:host {
  --header-spacing-xs: #{var-rem(--semantic-spacing-xs)};
  --wink-logo-width: #{wink-rem(4)};
  --wink-dropdown-font-size: #{wink-rem(1.4)};
  --wink-dropdown-color: var(--semantic-color-text-subtlest);
  --wink-icon-color: var(--semantic-color-icon-subtle);
  --wink-global-header-bg: var(--semantic-components-global-header-color-background-navigation-default);
  --wink-icon-bg-color: transparent;

  @include media('from-md') {
    --wink-dropdown-bg-color: transparent;
  }

  position: fixed;
  z-index: $z-index--global-header;
  top: 0;
  display: block;
  width: 100%;

  .wrapper-external {
    position: relative;
    width: 100%;

    &::after {
      pointer-events: none;
      content: '';
      position: absolute;
      z-index: -2;
      inset: 0;
      display: block;
      width: 100%;
      height: 100%;
      background-color: var(--wink-global-header-bg, var(--semantic-components-global-header-color-background-navigation-default));
      backdrop-filter: blur(var-rem(--semantic-components-global-header-blur-navigation-blur));
    }
  }

  .wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    align-content: stretch;
    box-sizing: border-box;
    max-width: $default-max-site-width;
    margin: auto;

    .hidden-when-scrolled {
      display: none;
    }
  }

  .mobile-row {
    position: sticky;
    z-index: 100;
    top: 0;
    right: 0;
    left: 0;
    display: flex;
    flex-flow: row nowrap;
    gap: var-rem(--semantic-spacing-md);
    place-content: center space-between;
    align-items: center;
    height: wink-rem(4);
    padding: var-rem(--semantic-spacing-sm) var-rem(--semantic-spacing-md);
    border-bottom: 1px solid var(--semantic-color-border-subtle);

    .mobile-fly-out {
      display: none;
    }

    &.open {
      border-bottom: none;

      .mobile-row-right {
        ::slotted([slot='mobile-search']),
        ::slotted([slot='mobile-customer-zone']) {
          display: none;
        }
      }

      .mobile-fly-out {
        position: fixed;
        z-index: 200;
        inset: 0;
        top: wink-rem(6.4);
        transform-origin: top right;
        overflow: auto;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        padding: var-rem(--semantic-spacing-lg) var-rem(--semantic-spacing-md) var-rem(--semantic-spacing-lg);
        background-color: var(--semantic-components-global-header-color-background-flyout-default);
        opacity: 0;
        visibility: hidden;
        animation: fade-in 300ms ease-in-out forwards;

        .mobile-fly-out-top {
          position: relative;
          isolation: isolate;
          overflow: hidden;
          flex: 1;

          &-main {
            position: relative;
            overflow: auto;
            display: flex;
            flex-direction: column;
            gap: var-rem(--semantic-spacing-lg);
            visibility: hidden;
            animation: 1s fade-in;
            animation-fill-mode: forwards;
            background-color: var(--semantic-components-global-header-color-background-flyout-default);
            width: 100%;
          }

          &-sub-nav {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: 1;
            background-color: var(--semantic-components-global-header-color-background-flyout-default);
            transform: translateX(100%);
            transition: transform 0.2s ease-in;
            overflow: auto;
            display: flex;
            flex-direction: column;
            will-change: transform;

            &.open {
              transform: translateX(0%);
              transition-timing-function: ease-out;
            }
          }
        }

        .mobile-fly-out-bottom {
          display: flex;
          flex-direction: row;
          gap: var-rem(--semantic-spacing-md);
          visibility: hidden;
          animation: 1s fade-in;
          animation-fill-mode: forwards;

          ::slotted([slot='mobile-customer-orientation']) {
            display: flex;
            flex-direction: row;
            gap: var-rem(--semantic-spacing-md);
          }
        }
      }
    }

    .mobile-row-right {
      display: flex;
      flex-flow: row nowrap;
      gap: var-rem(--semantic-spacing-md);
      align-content: center;
      align-items: center;

      ::slotted([slot='main-navigation']) {
        display: contents;
      }
    }

    .mobile-hamburger {
      position: relative;
      width: wink-rem(4);
      height: wink-rem(4);

      .mobile-hamburger-button {
        cursor: pointer;
        position: absolute;
        z-index: 1000;
        top: 0;
        left: 0;
        display: block;
        width: wink-rem(4);
        height: wink-rem(4);
        margin: 0;
        padding: unset;
        border: unset;
        background: unset;
        -webkit-touch-callout: none;
      }

      .mobile-hamburger {
        z-index: 1000;
        margin: 0;
      }

      wink-icon.mobile-hamburger-button {
        z-index: 500;
      }

      .mobile-hamburger-button:focus ~ wink-icon {
        @include state-focus-outline;

        outline-offset: calc(wink-rem(0.3) * -1);
      }
    }

    ::slotted([slot='mobile-brand']) {
      z-index: 500;
    }
  }

  .flyout-container {
    display: contents;

    @include media('from-md') {
      position: relative;
      top: 100%;
      right: 0;
      left: 0;
      display: grid;
      max-width: $default-max-site-width;
      transition: all 0.3s ease;

      &::after {
        content: '';
        position: absolute;
        z-index: var(--pannel-after-z-index, -1);
        top: var(--after-top, 0);
        left: 50%;
        transform: translateX(-50%);
        width: 100vw;
        height: 100%;
        background-color: var(--semantic-components-global-header-color-background-flyout-default);
      }

      > ::slotted([slot='flyout']) {
        grid-area: 1 / 1 / 2 / 2;
      }

      .line {
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        width: 100vw;
        border-bottom: wink-rem(0.1) solid var(--semantic-color-border-subtle);
      }
    }
  }

  .breadcrumbs {
    position: relative;

    &--inner {
      padding: var-rem(--semantic-spacing-md);
      max-width: $default-max-site-width;
      margin: auto;

      @include media('from-md') {
        padding: var-rem(--semantic-spacing-sm) 0;
      }
    }

    &::after {
      pointer-events: none;
      content: '';
      position: absolute;
      z-index: -2;
      inset: 0;
      display: block;
      width: 100%;
      height: 100%;
      background-color: var(--semantic-components-global-header-color-background-flyout-default);
      backdrop-filter: blur(var-rem(--semantic-components-global-header-blur-navigation-blur));
    }

    &-hidden {
      visibility: hidden;
      height: 0;
      overflow: hidden;
    }
  }

  .top-row,
  .flyout-container,
  .bottom-row {
    display: none;
  }

  .top-row,
  .bottom-row,
  .flyout-container,
  .mobile-row {
    gap: var(--header-spacing-xs);
    justify-content: space-between;
  }

  ::slotted([slot='main-navigation']),
  ::slotted([slot='customer-orientation']) {
    margin-right: auto;
  }

  ::slotted([slot='search']) {
    margin-left: auto;

    @include media-header('from-md') {
      flex: 0 0 wink-rem(16);
      width: wink-rem(16);
    }
  }

  ::slotted([slot='call-to-action']),
  ::slotted([slot='mobile-call-to-action']) {
    display: flex;
    gap: var(--header-spacing-xs);
  }

  ::slotted([slot='mobile-call-to-action']) {
    flex-direction: column;
  }

  ::slotted([slot='brand']) {
    padding: var(--header-spacing-xs) 0;
  }

  ::slotted([slot='customer-zone']),
  ::slotted([slot='utility-bar']) {
    padding-top: wink-rem(0.3);
  }

  @media only screen and (min-width: header-breakpoint('md')) and (max-width: calc(breakpoint('lg') - 1px)) {
    --wink-button-font-size: #{var-rem(--semantic-typography-font-size-2sm)};
    --wink-button-line-height: #{var-rem(--semantic-typography-line-height-2sm)};
  }

  @include media-header('until-md') {
    --wink-button-font-size: #{var-rem(--semantic-typography-font-size-sm)};
    --wink-button-line-height: #{var-rem(--semantic-typography-line-height-sm)};
  }
  @include media-header('from-md') {
    --wink-button-font-size: #{var-rem(--semantic-typography-font-size-2sm)};
    --wink-button-line-height: #{var-rem(--semantic-typography-line-height-2sm)};
  }
  @include media-header('from-md') {
    --wink-logo-width: #{wink-rem(4.8)};

    ::slotted([slot='main-navigation']) {
      display: contents;
      margin-right: auto;
    }

    ::slotted([slot='brand']) {
      @include media-header('from-md') {
        margin-right: var-rem(--semantic-spacing-md);
      }
    }

    .mobile-row {
      display: none;
    }

    .top-row,
    .bottom-row {
      display: flex;
      align-items: center;
    }

    .top-row {
      padding-bottom: var(--header-spacing-xs);
    }

    .flyout-container {
      display: block;
    }
  }
}

:host(.compact) {
  flex-direction: row-reverse;

  .bottom-row {
    flex-grow: 1;
  }
}

:host(.minimal) {
  .mobile-hamburger {
    position: relative;
    width: auto;
    height: auto;

    .mobile-hamburger-button.open {
      display: none;
    }

    .mobile-fly-out {
      display: block;
    }
  }
}

:host([background-color='brand']) {
  @include media-header('from-md') {
    --wink-global-header-bg: var(--semantic-color-background-canvas-brand);
  }
}

:host([background-color='brand-alt']) {
  @include media-header('from-md') {
    --wink-global-header-bg: var(--semantic-color-background-canvas-accent);
  }
}

@keyframes grow-down {
  0% {
    transform: scaleY(0);
  }

  80% {
    transform: scaleY(1.1);
  }

  100% {
    transform: scaleY(1);
  }
}
@keyframes fade-in {
  0% {
    opacity: 0;
  }

  100% {
    visibility: visible;
    opacity: 1;
  }
}
